import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { IonButton, IonIcon } from '@ionic/angular/standalone';

import { TranslatePipe } from '../../core/i18n';
import { BiometricService } from '../../public/auth/services/biometric.service';
import { CredentialVaultService } from '../../public/auth/services/credential-vault.service';
import { LocalAuthService, LockoutInfo } from '../../public/auth/services/local-auth.service';
import { HapticsService } from '../../services/haptics.service';
import { LogoComponent } from '../logo/logo.component';
import { PinKeypadComponent } from '../pin-keypad/pin-keypad.component';

@Component({
  selector: 'app-pin-entry',
  standalone: true,
  imports: [CommonModule, IonButton, IonIcon, PinKeypadComponent, LogoComponent, TranslatePipe],
  templateUrl: './pin-entry.component.html',
  styleUrls: ['./pin-entry.component.scss'],
})
export class PinEntryComponent implements AfterViewInit, OnDestroy {
  private readonly localAuth = inject(LocalAuthService);
  private readonly biometricService = inject(BiometricService);
  private readonly vault = inject(CredentialVaultService);
  private readonly haptics = inject(HapticsService);

  @Output() unlocked = new EventEmitter<void>();
  @Output() forgotPin = new EventEmitter<void>();

  readonly pin = signal('');
  readonly error = signal('');
  readonly isProcessing = signal(false);
  readonly lockoutInfo = signal<LockoutInfo | null>(null);
  readonly lockoutTimer = signal('');
  readonly biometricAvailable = signal(false);
  readonly biometricType = signal('');
  readonly showSuccess = signal(false);

  private lockoutInterval: ReturnType<typeof setInterval> | null = null;

  async ngAfterViewInit(): Promise<void> {
    await this.checkLockout();
    await this.checkBiometric();

    // Auto-trigger biometric if available and not locked out
    if (this.biometricAvailable() && !this.lockoutInfo()?.isLocked) {
      void this.onBiometric();
    }
  }

  ngOnDestroy(): void {
    this.clearLockoutTimer();
  }

  onDigit(digit: string): void {
    if (this.pin().length >= 4 || this.isProcessing() || this.lockoutInfo()?.isLocked) return;
    this.error.set('');
    this.pin.update((p) => p + digit);

    if (this.pin().length === 4) {
      void this.verifyPin();
    }
  }

  onDelete(): void {
    if (this.isProcessing()) return;
    this.pin.update((p) => p.slice(0, -1));
    this.error.set('');
  }

  async onBiometric(): Promise<void> {
    if (this.isProcessing()) return;

    this.isProcessing.set(true);
    const success = await this.localAuth.unlockWithBiometric();
    this.isProcessing.set(false);

    if (success) {
      await this.handleSuccess();
    }
    // If biometric cancelled, just stay on PIN screen (no error)
  }

  onForgotPin(): void {
    this.forgotPin.emit();
  }

  private async verifyPin(): Promise<void> {
    this.isProcessing.set(true);
    const success = await this.localAuth.verifyPin(this.pin());
    this.isProcessing.set(false);

    if (success) {
      await this.handleSuccess();
    } else {
      await this.haptics.error();
      this.pin.set('');

      // Check if now locked out
      const lockout = await this.localAuth.checkLockout();
      if (lockout.isLocked) {
        this.lockoutInfo.set(lockout);
        this.startLockoutTimer(lockout.remainingSeconds);
      } else {
        this.error.set('pin.incorrectPin');
        if (lockout.attemptsRemaining <= 3) {
          this.error.set('pin.attemptsRemaining');
        }
      }
    }
  }

  private async handleSuccess(): Promise<void> {
    this.showSuccess.set(true);
    await this.haptics.success();

    // Brief success animation, then check token refresh
    await this.delay(200);

    this.isProcessing.set(true);
    await this.localAuth.refreshTokenIfNeeded();
    this.isProcessing.set(false);

    this.unlocked.emit();
  }

  private async checkLockout(): Promise<void> {
    const lockout = await this.localAuth.checkLockout();
    if (lockout.isLocked) {
      this.lockoutInfo.set(lockout);
      this.startLockoutTimer(lockout.remainingSeconds);
    }
  }

  private async checkBiometric(): Promise<void> {
    const biometricEnabled = await this.vault.isBiometricEnabled();
    if (!biometricEnabled) return;

    const status = await this.biometricService.checkAvailability();
    if (status.isAvailable) {
      this.biometricAvailable.set(true);
      this.biometricType.set(this.biometricService.getBiometryTypeName(status.biometryType));
    }
  }

  private startLockoutTimer(remainingSeconds: number): void {
    this.clearLockoutTimer();
    this.updateLockoutDisplay(remainingSeconds);

    let remaining = remainingSeconds;
    this.lockoutInterval = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        this.clearLockoutTimer();
        this.lockoutInfo.set(null);
        this.lockoutTimer.set('');
      } else {
        this.updateLockoutDisplay(remaining);
      }
    }, 1000);
  }

  private updateLockoutDisplay(seconds: number): void {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    this.lockoutTimer.set(mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`);
  }

  private clearLockoutTimer(): void {
    if (this.lockoutInterval) {
      clearInterval(this.lockoutInterval);
      this.lockoutInterval = null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
